import { useCallback, memo } from 'react';
import Link from '@/components/smart-link';
import cn from 'classnames';
import { HoverCard } from '@sharpe/ui/catalyst';
import { LogoJupyter, Folder, Chat } from '@carbon/icons-react';
import { formatRelativeTime } from '@sharpe/shared/format';
import type { WorkspaceWithChats } from '@/components/use-workspaces';
import { useOpenJupyter } from '@/hooks/use-open-jupyter';
import { usePathname } from '@/hooks/use-routing';

interface SidebarWorkspaceItemProps {
  workspace: WorkspaceWithChats;
  isNew?: boolean;
}

export const SidebarWorkspaceItem = memo(function SidebarWorkspaceItem({
  workspace,
  isNew = false,
}: SidebarWorkspaceItemProps) {
  const { openJupyter, isLoading: isJupyterLoading } = useOpenJupyter();
  const pathname = usePathname();

  const hasMultipleChats = workspace.chats.length > 1;
  const activeChatId = workspace.chats.find((chat) => pathname === `/chat/${chat.id}`)?.id;
  const firstChat = workspace.chats[0];
  const href =
    hasMultipleChats || !firstChat ? `/workspace/${workspace.id}` : `/chat/${firstChat.id}`;
  const isActive = !!activeChatId;

  const handleOpenJupyter = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openJupyter(workspace.workspaceDir);
    },
    [openJupyter, workspace.workspaceDir]
  );

  const rowClasses = cn(
    'group flex items-center h-8 px-2 rounded-sm transition-colors duration-150 no-underline',
    'text-[13px] !text-foreground/70 hover:!text-foreground',
    'hover:bg-muted-foreground/10',
    isNew && 'animate-highlight-fade',
    isActive && 'bg-muted-foreground/10 !text-foreground'
  );

  const rowContent = (
    <Link href={href} className={rowClasses}>
      <span className="truncate flex-1">{workspace.title || 'Untitled'}</span>
      {hasMultipleChats && (
        <span className="text-[11px] text-muted-foreground/40 tabular-nums shrink-0 ml-2">
          {workspace.chats.length}
        </span>
      )}
    </Link>
  );

  // Only show preview tooltip for workspaces with multiple chats
  if (!hasMultipleChats) {
    return <div className="mx-2">{rowContent}</div>;
  }

  // Sort chats by updatedAt descending (most recent first)
  const sortedChats = [...workspace.chats].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  return (
    <div className="mx-2">
      <HoverCard
        anchor="right start"
        content={({ close }) => (
          <>
            {/* Workspace header */}
            <div className="px-3 py-2.5 border-b border-border/30">
              <div className="text-sm font-medium text-foreground truncate">
                {workspace.title || 'Untitled'}
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground/50">
                <span className="flex items-center gap-1">
                  <Chat size={12} />
                  {workspace.chats.length} chats
                </span>
                <span>Updated {formatRelativeTime(workspace.updatedAt)}</span>
              </div>
            </div>

            {/* Chat list */}
            <div className="max-h-[240px] overflow-y-auto">
              {sortedChats.map((chat) => {
                const isChatActive = chat.id === activeChatId;
                return (
                  <Link
                    key={chat.id}
                    href={`/chat/${chat.id}`}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 text-[13px] no-underline transition-colors',
                      isChatActive
                        ? 'bg-muted-foreground/10 !text-foreground'
                        : '!text-foreground/60 hover:!text-foreground hover:bg-muted-foreground/5'
                    )}
                    onClick={close}
                  >
                    <span className="truncate flex-1">{chat.title || 'Untitled'}</span>
                    <span className="text-xs text-muted-foreground/30 tabular-nums shrink-0">
                      {formatRelativeTime(chat.updatedAt)}
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Actions footer */}
            <div className="flex items-center border-t border-border/30">
              <Link
                href={`/workspace/${workspace.id}`}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs !text-muted-foreground/60 hover:!text-foreground hover:bg-muted-foreground/5 transition-colors no-underline"
                onClick={close}
              >
                <Folder size={14} />
                <span>Open Workspace</span>
              </Link>
              <div className="w-px h-5 bg-border/30" />
              <button
                onClick={(e) => {
                  handleOpenJupyter(e);
                  close();
                }}
                disabled={isJupyterLoading}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs',
                  'text-muted-foreground/60 hover:text-jupyter hover:bg-muted-foreground/5 transition-colors',
                  isJupyterLoading && 'opacity-50 cursor-wait'
                )}
              >
                <LogoJupyter size={14} />
                <span>Open Jupyter</span>
              </button>
            </div>
          </>
        )}
        className="min-w-[280px] max-w-[320px] !p-0"
      >
        {rowContent}
      </HoverCard>
    </div>
  );
});
